import { Badge, Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconHierarchy3 } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { memo, useEffect, useRef, useState } from 'react'

import { confirmLiveModuleAction, LiveModuleBanner } from '@/components/LiveModuleBanner'
import { Table } from '@/components/Table'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { StepWithValidation } from '@/hooks/useStepsWithValidation'
import { AnalyzingBadge } from '@/screens/ContentModuleFormScreen/AnalyzingBadge'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { ReviewStepModal } from '@/screens/ContentModuleFormScreen/ReviewStepModal'
import { UploadStepModal } from '@/screens/ContentModuleFormScreen/UploadStepModal'
import type { GetStepsResponse } from '@/services/api/api.types'
import { castStepToZodForm } from '@/utils/contentModuleForm'

// enum SplittingMode {
//   Unknown = 'unknown',
//   Single = 'single',
//   Auto = 'auto',
//   Manual = 'manual',
// }

export const StepsSection = memo(function StepsSection(props: CMFormSection) {
  // const [splittingMode, setSplittingMode] = useState<SplittingMode>(SplittingMode.Unknown)
  const [hasSteps, setHasSteps] = useState(false)
  const [localSteps, setLocalSteps] = useState<StepWithValidation[]>([])
  const pendingReordersRef = useRef(0)
  const { api } = useGlobalState()

  useEffect(() => {
    if (props.stepsQuery.data && pendingReordersRef.current === 0) {
      setLocalSteps(props.stepsQuery.data)
    }
  }, [props.stepsQuery.data])

  const reorderStepsMutation = useMutation({
    mutationFn: api.reorderSteps,
    onSuccess: () => {
      props.stepsQuery.refetch().finally(() => {
        pendingReordersRef.current--
      })
    },
    onError: error => {
      pendingReordersRef.current--
      console.error('Failed to reorder steps:', error)
      if (props.stepsQuery.data) {
        setLocalSteps(props.stepsQuery.data)
      }
    },
  })

  useEffect(() => {
    setHasSteps(!!localSteps.length)
  }, [localSteps])

  const modalStyles = {
    styles: {
      body: {
        paddingTop: theme.rem(16),
        backgroundColor: theme.colors.gray[0],
      },
    },
  }

  function openUploadModal() {
    props.openModal?.({
      Component: UploadStepModal,
      ModalProps: modalStyles,
    })
  }

  function openReviewModal() {
    props.openModal?.({
      Component: ReviewStepModal,
      ModalProps: modalStyles,
    })
  }

  const addNewContent = async () => {
    if (props.isLive) {
      const confirmed = await confirmLiveModuleAction({
        body: 'When you save this new file, it will immediately be available to your End Users in the app. Do you want to continue?',
      })
      if (!confirmed) return
    }
    stageStep()
    openUploadModal()
  }

  // async function handleSplittingModeSelection(v: SplittingMode) {
  //   setSplittingMode(v)

  //   if (v === SplittingMode.Single) {
  //     uiStore.showLoadingOverlay()
  // Handle row reordering with immediate local state update
  const handleRowOrderChange = (newData: StepWithValidation[]) => {
    if (!props.taskId) return

    pendingReordersRef.current++
    setLocalSteps(newData)

    const stepIds = newData.map(step => step.stepId)
    reorderStepsMutation.mutate({
      task_id: props.taskId,
      step_ids: stepIds,
    })
  }

  async function deleteStep(data: GetStepsResponse['0']) {
    modals.openConfirmModal({
      title: props.isLive ? undefined : 'Delete content?',
      withCloseButton: !props.isLive,
      centered: true,
      children: props.isLive ? (
        <Stack gap="md">
          <LiveModuleBanner />
          <Text size="sm">
            Deleting this file will immediately remove it for End Users in the app. This action cannot be undone.
          </Text>
        </Stack>
      ) : (
        <Text size="sm">
          Are you sure you want to delete this content? This action is destructive and you will lose all of the progress
          made for this content.
        </Text>
      ),
      labels: { confirm: 'Delete Content', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => {
        props.stepForm.setValues(castStepToZodForm())
        props.stepDeleteMutation?.mutate({ step_id: data.stepId, task_id: props.taskId! })
      },
    })
  }

  function stageStep(data?: GetStepsResponse['0']) {
    props.stepForm.setValues(castStepToZodForm(data))
    props.stepForm.resetDirty()
    props.stepForm.resetTouched()
  }

  return (
    <Stack gap="xxxl">
      {/* {!hasSteps && (
        <Box>
          <InputLabel
            label="Section Creator"
            description="Choose whether you want the system to organize your content into sections or if you want to do it yourself. Single section is easiest, but multiple sections will help the Sidekick guide users through your content."
            withAsterisk
          />

          <Grid gutter="xl" align="stretch" mt={theme.rem(8)}>
            <Grid.Col span={4}>
              <Button
                onClick={() => handleSplittingModeSelection(SplittingMode.Single)}
                fullWidth
                size="lg"
                classNames={classes.buttonWithSecondaryText}>
                Single Section
                <Text component="span" size="xs">
                  All content in one section
                </Text>
              </Button>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap="xs" align="center">
                <AIActionButton
                  variant="button"
                  onClick={() => handleSplittingModeSelection(SplittingMode.Auto)}
                  size="lg"
                  fullWidth
                  disabled
                  classNames={classes.buttonWithSecondaryText}>
                  Multiple Sections
                  <Text component="span" size="xs">
                    Suggest sections automatically.
                  </Text>
                </AIActionButton>
                <ComingSoon />
              </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
              <Stack gap="xs" align="center">
                <Button
                  onClick={() => handleSplittingModeSelection(SplittingMode.Manual)}
                  size="lg"
                  fullWidth
                  disabled
                  classNames={classes.buttonWithSecondaryText}>
                  DIY Sections
                  <Text component="span" size="xs">
                    Create your sections manually.
                  </Text>
                </Button>
                <ComingSoon />
              </Stack>
            </Grid.Col>
          </Grid>
        </Box>
      )} */}

      <Box>
        {hasSteps && (
          <Table
            data={localSteps}
            columns={stepColDefs}
            rowActionsInclude={['edit', 'delete']}
            enableTopToolbar={false}
            enableRowOrdering={true}
            onRowActionClick={async (action, row) => {
              if (action === 'delete') {
                deleteStep(row.original)
              } else if (action === 'edit') {
                const isProcessing = row.original.inProcess === true
                if (isProcessing) return
                if (props.isLive) {
                  const confirmed = await confirmLiveModuleAction({
                    body: 'Edits to this file will be immediately available to your End Users in the app. Do you want to continue?',
                  })
                  if (!confirmed) return
                }
                stageStep(row.original)
                openReviewModal()
              }
            }}
            rowActionsTooltipLabels={row => ({
              edit: row.original.inProcess ? 'Content is still being analyzed' : 'Review File',
              delete: 'Delete File',
            })}
            rowActionsButtonProps={row => ({
              edit: { disabled: row.original.inProcess === true },
            })}
            EmptyStateProps={{
              title: 'No Files Added',
              description: `Build the outline for your module and add content files.`,
              Icon: IconHierarchy3,
            }}
            displayColumnDefOptions={{ 'mrt-row-actions': { size: 98 } }}
            onRowOrderChange={handleRowOrderChange}
          />
        )}

        <Group justify="center" mt="xl">
          <Stack gap="xs" align="center">
            <Button
              loading={
                props.cmContentQuery.isFetching ||
                props.cmCreateMutation.isPending ||
                props.stepCreateMutation.isPending
              }
              onClick={() => {
                addNewContent()
              }}>
              {hasSteps ? 'Add Another File' : 'Add File'}
            </Button>
            {hasSteps}
          </Stack>
        </Group>
      </Box>

      {/* TODO: Re-enable when we have multiple sections */}
      {/* <Grid gutter="xxxl">
        <Grid.Col span={12}>
          <Radio.Group label="Module Flow" withAsterisk {...props.moduleForm.getInputProps('presentationMode')}>
            <Group gap="xl" align="stretch">
              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.member_directed}
                label="User Directed"
                description="Users can access the content in any order."
              />

              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.sequential}
                label={<ComingSoon prefix="Sequential " dimPrefix />}
                disabled
                description="Their first time through, the user must go through the content in the order it's laid out."
              />
            </Group>
          </Radio.Group>
        </Grid.Col>
      </Grid> */}
    </Stack>
  )
})

const UPPERCASE_TYPES = new Set(['url', 'pdf', 'pptx', 'docx'])
const SPECIAL_CASE_TYPES: Record<string, string> = { youtube: 'YouTube' }

function formatContentType(type?: string): string {
  if (!type) return '—'
  if (SPECIAL_CASE_TYPES[type]) return SPECIAL_CASE_TYPES[type]
  if (UPPERCASE_TYPES.has(type)) return type.toUpperCase()
  return type.charAt(0).toUpperCase() + type.slice(1)
}

const stepColDefs: MRT_ColumnDef<StepWithValidation>[] = [
  {
    accessorKey: 'name',
    header: 'File Name',
    Cell: props => (
      <Text size="sm" fw={500}>
        {props.renderedCellValue || 'Untitled'}
      </Text>
    ),
    size: 280,
    grow: true,
  },
  {
    id: 'contentType',
    header: 'File Type',
    accessorFn: row => formatContentType(row.contentType),
    grow: false,
    size: 100,
  },
  // {
  //   accessorKey: 'title',
  //   header: 'Section Overview',
  //   Cell: props => <Text size="sm">{props.renderedCellValue}</Text>,
  //   grow: true,
  // },
  {
    id: 'status',
    header: 'File Status',
    grow: false,
    size: 158,
    Cell: props => {
      const { inProcess, processProgress, processProgressError, transcodeCompleted, transcodeSuccess, valid } =
        props.row.original

      if (processProgressError) {
        return (
          <Tooltip
            label="There was an issue processing this file. This may be due to the file being too large or in an unsupported format."
            withArrow
            position="top"
            multiline
            maw={300}>
            <Badge color="red" variant="filled">
              Error
            </Badge>
          </Tooltip>
        )
      }

      if (inProcess) {
        return <AnalyzingBadge progress={processProgress} />
      }

      if (transcodeCompleted === false) {
        return (
          <Badge color="yellow" variant="filled">
            Preparing
          </Badge>
        )
      }

      if (transcodeCompleted === true && transcodeSuccess === false) {
        return (
          <Badge color="red" variant="filled">
            Transcode Error
          </Badge>
        )
      }

      if (!valid) {
        return (
          <Badge color="orange" variant="filled">
            Review Needed
          </Badge>
        )
      }

      return (
        <Badge color="green" variant="filled">
          Ready to Submit
        </Badge>
      )
    },
  },
]
